/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// INTERFACES
import { AdapterInterface } from "../../interfaces/Adapter.interface";
// CONSTANTS
import { BLANK_ADAPTER } from "../../constants/adapter";
import { FormAction } from "../../types/enums";

/** Where the adapter form's Overlap Analysis report opens. */
export type OverlapAnalysisTarget = "panel" | "window";

/**
 * localStorage key for the Overlap Analysis destination.
 *
 * Bare camelCase, matching `endpointMonitorOpen` — the convention this app's
 * other remembered panel preference already uses.
 */
export const OVERLAP_ANALYSIS_OPEN_IN_KEY = "adapterOverlapAnalysisOpenIn";

/**
 * Read the stored destination preference.
 *
 * Guarded because this runs at module scope: localStorage throws in private
 * browsing and in a non-browser runner, and an unguarded read there takes down
 * the whole store rather than just this flag. Anything that is not exactly
 * "window" reads as "panel", so a corrupted value degrades to the destination
 * that cannot be blocked rather than to one that may never open.
 */
function readOverlapAnalysisTarget(): OverlapAnalysisTarget {
  try {
    return localStorage.getItem(OVERLAP_ANALYSIS_OPEN_IN_KEY) === "window" ? "window" : "panel";
  } catch {
    return "panel";
  }
}

/**
 * State of the adapter slice: the adapter currently being viewed or edited,
 * the active selection, and the open/closed flags for this area's modals.
 */
export interface AdapterState {
  adapter: AdapterInterface;
  openDrawerAdapter: boolean;
  openModalCreateNewAdapter: boolean;
  adapterRefreshTable: boolean;
  openModalDeleteAdapter: boolean;
  formCreateEdit: FormAction;
  openDrawerAssignActions: boolean;
  /** Where the Overlap Analysis report opens: over the form, or its own window. */
  overlapAnalysisOpenIn: OverlapAnalysisTarget;
}

const initialState: AdapterState = {
  adapter: { ...BLANK_ADAPTER },
  openDrawerAdapter: false,
  openModalCreateNewAdapter: false,
  adapterRefreshTable: false,
  openModalDeleteAdapter: false,
  formCreateEdit: FormAction.Create,
  openDrawerAssignActions: false,
  overlapAnalysisOpenIn: readOverlapAnalysisTarget(),
};

export const adapterSlice = createSlice({
  name: "adapter",
  initialState,
  reducers: {
    updateAdapter: (state, action: PayloadAction<AdapterInterface>) => {
      state.adapter = action.payload;
    },
    updateOpenDrawerAdapter: (state, action: PayloadAction<boolean>) => {
      state.openDrawerAdapter = action.payload;
    },
    updateOpenModalCreateNewAdapter: (state, action: PayloadAction<boolean>) => {
      state.openModalCreateNewAdapter = action.payload;
    },
    updateAdapterRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.adapterRefreshTable = action.payload;
    },
    updateOpenModalDeleteAdapter: (state, action: PayloadAction<boolean>) => {
      state.openModalDeleteAdapter = action.payload;
    },
    updateFormCreateEdit: (state, action: PayloadAction<FormAction>) => {
      state.formCreateEdit = action.payload;
    },
    updateOpenDrawerAssignActions: (state, action: PayloadAction<boolean>) => {
      state.openDrawerAssignActions = action.payload;
    },
    updateOverlapAnalysisOpenIn: (state, action: PayloadAction<OverlapAnalysisTarget>) => {
      state.overlapAnalysisOpenIn = action.payload;
      try {
        localStorage.setItem(OVERLAP_ANALYSIS_OPEN_IN_KEY, action.payload);
      } catch {
        // The preference is best-effort. A browser that refuses to store it
        // still honours the choice for this session; failing the dispatch
        // would instead break the button that set it.
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateAdapter,
  updateOpenDrawerAdapter,
  updateOpenModalCreateNewAdapter,
  updateAdapterRefreshTable,
  updateOpenModalDeleteAdapter,
  updateFormCreateEdit,
  updateOpenDrawerAssignActions,
  updateOverlapAnalysisOpenIn,
} = adapterSlice.actions;

export default adapterSlice.reducer;
